import { Fragment, useMemo } from 'react';
import { Divider, Paper, Stack, Typography } from '@mui/material';

import {
  AccessRequestClientModel,
  AccessRequestClientModelChallenge,
  AccessRequestsScopeModel,
  AccessStatus,
  accessStatusesPendingApproval,
  ChallengeApproverClientModel,
  ClientChallengeLogicalRelation,
  ClientChallengeStatus,
  ClientChallengeType,
} from '@apono/client-app-sdk';
import { MaterialIcon } from '@common/ui/MaterialIcon/MaterialIcon';
import RequestApproval from '@MyTasks/organisms/RequestApproval';
import useRequestsTable from '@common/hooks/use-requests-table';

export default function RequestApprovals({
  request,
  challenge,
}: {
  request: AccessRequestClientModel;
  challenge: AccessRequestClientModelChallenge;
}) {
  const { pageData } = useRequestsTable({
    statuses: accessStatusesPendingApproval,
    scope: AccessRequestsScopeModel.MyTasks,
  });

  const isCanApprove = useMemo(() => pageData?.data.some((req) => req.id === request.id), [pageData, request]);

  return (
    <>
      <Stack spacing={1}>
        <Typography variant="subtitle2">Approvals:</Typography>
        <Paper variant="details">
          <Stack spacing={1}>
            {challenge?.approvers.map((approver, i) => (
              <Fragment key={approver.name}>
                <Approver approver={approver} />
                {request.challenge && i < request.challenge.approvers.length - 1 && (
                  <Relation relation={request.challenge.logical_relation} />
                )}
              </Fragment>
            ))}
            {isCanApprove && (
              <>
                <Divider />
                <RequestApproval request={request} />
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
    </>
  );
}

function Approver({ approver }: { approver: ChallengeApproverClientModel }) {
  const icon = useMemo(() => {
    switch (approver.type) {
      case ClientChallengeType.User:
        return 'person';
      case ClientChallengeType.Group:
        return 'group';
      case ClientChallengeType.Manager:
        return 'admin_panel_settings';
      case ClientChallengeType.Shift:
      case ClientChallengeType.ContextGroup:
        return 'badge';
      default:
        return '';
    }
  }, [approver]);

  const isRejected = approver.status === AccessStatus.Rejected;
  const rejectionReason = useMemo(
    () => (isRejected ? approver.metadata.approver_reason : undefined),
    [approver, isRejected],
  );

  return useMemo(
    () => (
      <Stack direction={'column'} spacing={1}>
        <Stack spacing={0.5} direction="row" alignItems="center">
          <Status status={approver.status} />
          <div>
            <MaterialIcon symbol={icon} sx={{ display: 'block', fontSize: '1.3rem' }} />
          </div>
          <Typography variant="body2" component="div">
            {approver.name}
          </Typography>
          {approver.metadata.email && (
            <Typography variant="inputLabel" color="text.muted" component="div">
              ({approver.metadata.email})
            </Typography>
          )}
        </Stack>
        {isRejected && rejectionReason && (
          <Stack direction="row" spacing={0.5}>
            <Typography variant="helperText" color="error">
              Rejection Reason:
            </Typography>
            <Typography variant="helperText">
              {rejectionReason}
            </Typography>
          </Stack>
        )}
      </Stack>
    ),
    [approver, icon, isRejected, rejectionReason],
  );
}

function Relation({ relation }: { relation?: ClientChallengeLogicalRelation }) {
  return useMemo(
    () => (
      <Divider textAlign="left">
        <Typography variant="inputLabel" color="text.muted">
          {relation === ClientChallengeLogicalRelation.AllOf ? 'AND' : 'OR'}
        </Typography>
      </Divider>
    ),
    [relation],
  );
}

function Status({ status }: { status: ChallengeApproverClientModel['status'] }) {
  const text = useMemo(() => {
    switch (status) {
      case ClientChallengeStatus.Pending:
        return 'Pending for approval from';
      case ClientChallengeStatus.Approved:
        return 'Approved by';
      case ClientChallengeStatus.Rejected:
        return 'Rejected by';
    }
  }, [status]);

  const color = useMemo(() => {
    switch (status) {
      case ClientChallengeStatus.Pending:
        return 'warning.main';
      case ClientChallengeStatus.Approved:
        return 'success.main';
      case ClientChallengeStatus.Rejected:
        return 'error.main';
    }
  }, [status]);

  return (
    <Typography variant="inputLabel" color={color}>
      {text}
    </Typography>
  );
}
